import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { Cross } from '../../../../assets/svgs/icons';
import { SelectSquare } from '../../../../components/Select/SelectSquare/SelectSquare';
import { MultiplySelectSquare } from '../../../../components/Select/MultiplySelectSquare';

export const SocialMediaAuthPopover = ({
  isOpen,
  onClose,
  network,
  settings,
  facebookPages,
  tiktokCreatorInfo,
  tiktokPrivacyOptions,
  onUpdateSettings,
  onSave,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  if (!isOpen || !network) return null;

  const networkKey = network.key;
  const tiktokSettings = settings?.tiktok || {};
  const instagramSettings = settings?.instagram || {};
  const facebookSettings = settings?.facebook || {};

  const handleSave = () => {
    onSave && onSave(networkKey);
  };

  const handleUpdate = (updates) => {
    onUpdateSettings && onUpdateSettings(networkKey, updates);
  };

  const handleToggle = (field) => {
    handleUpdate((current) => {
      const nextValue = !current?.[field];
      if (field === 'music' && !nextValue) {
        return { [field]: nextValue, musicConfirmation: false };
      }
      return { [field]: nextValue };
    });
  };

  const handleContentDisclosure = () => {
    if (tiktokSettings?.contentDisclosure) {
      handleUpdate({
        contentDisclosure: false,
        yourBrand: false,
        brandedContent: false,
      });
      return;
    }
    handleUpdate({ contentDisclosure: true });
  };

  const handleYourBrand = () => {
    handleUpdate((current) => ({
      yourBrand: !current?.yourBrand,
    }));
  };

  const handleBrandedContent = () => {
    handleUpdate((current) => ({
      brandedContent: !current?.brandedContent,
    }));
  };

  const renderTiktokFields = () => (
    <div className={styles.formSection}>
      {tiktokCreatorInfo?.creator_nickname && (
        <div className={styles.helperLine}>
          {t('chatsocket.social_media_auth.connected_as', { nickname: tiktokCreatorInfo.creator_nickname })}
        </div>
      )}

      <div className={styles.formRow}>
        <label className={styles.fieldLabel}>{t('chatsocket.social_media_auth.video_title')}</label>
        <input
          className={styles.textInput}
          type="text"
          placeholder={t('chatsocket.social_media_auth.video_title_placeholder')}
          value={tiktokSettings?.headline || ''}
          onChange={(event) => handleUpdate({ headline: event.target.value })}
          disabled={isReadOnly}
        />
      </div>

      <div className={styles.formRow}>
        <label className={styles.fieldLabel}>{t('chatsocket.social_media_auth.privacy')}</label>
        <SelectSquare
          placeholder={t('chatsocket.social_media_auth.privacy_placeholder')}
          padding="10px 12px"
          border
          borderColor="#E3E3E3"
          backgroundColor="#F3F3F3"
          arrowDirection="down"
          options={tiktokPrivacyOptions || []}
          defaultSelected={
            tiktokPrivacyOptions?.find(
              (item) => item.value === tiktokSettings?.privacyLevel,
            ) || {
              label: '',
              value: '',
            }
          }
          onClickItem={(value) => handleUpdate({ privacyLevel: value?.value })}
          disabled={isReadOnly}
        />
      </div>

      <div className={styles.toggleGroup}>
        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.allow_comments')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.comment}
            disabled={isReadOnly || !!tiktokCreatorInfo?.comment_disabled}
            onChange={() => handleToggle('comment')}
          />
        </label>

        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.allow_duets')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.duet}
            disabled={isReadOnly || !!tiktokCreatorInfo?.duet_disabled}
            onChange={() => handleToggle('duet')}
          />
        </label>

        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.allow_stitch')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.stitch}
            disabled={isReadOnly || !!tiktokCreatorInfo?.stitch_disabled}
            onChange={() => handleToggle('stitch')}
          />
        </label>

        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.allow_music')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.music}
            disabled={isReadOnly}
            onChange={() => handleToggle('music')}
          />
        </label>

        {tiktokSettings?.music && (
          <label className={styles.toggleRow}>
            <span>{t('chatsocket.social_media_auth.confirm_music')}</span>
            <input
              type="checkbox"
              checked={!!tiktokSettings?.musicConfirmation}
              disabled={isReadOnly}
              onChange={() => handleToggle('musicConfirmation')}
            />
          </label>
        )}
      </div>

      <div className={styles.toggleGroup}>
        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.sponsored_content')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.contentDisclosure}
            disabled={isReadOnly}
            onChange={handleContentDisclosure}
          />
        </label>

        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.own_business')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.yourBrand}
            disabled={isReadOnly || !tiktokSettings?.contentDisclosure}
            onChange={handleYourBrand}
          />
        </label>

        <label className={styles.toggleRow}>
          <span>{t('chatsocket.social_media_auth.paid_partnership')}</span>
          <input
            type="checkbox"
            checked={!!tiktokSettings?.brandedContent}
            disabled={isReadOnly || !tiktokSettings?.contentDisclosure}
            onChange={handleBrandedContent}
          />
        </label>
      </div>
    </div>
  );

  const renderInstagramFields = () => (
    <div className={styles.formSection}>
      <div className={styles.formRow}>
        <label className={styles.fieldLabel}>{t('chatsocket.social_media_auth.publication_type')}</label>
        <MultiplySelectSquare
          backgroundColor="#F3F3F3"
          border
          borderColor="#E3E3E3"
          placeholder={t('chatsocket.social_media_auth.publication_type_placeholder')}
          arrowDirection="down"
          variante="gray"
          options={[
            { id: 2, label: t('chatsocket.social_media_auth.feed'), value: 'REELS' },
            { id: 3, label: t('chatsocket.social_media_auth.stories'), value: 'STORIES' },
          ]}
          fontSize={12}
          defaultSelected={instagramSettings?.instagramItems || []}
          onClickItem={(value) => handleUpdate({ instagramItems: value })}
          disabled={isReadOnly}
        />
      </div>
    </div>
  );

  const renderFacebookFields = () => (
    <div className={styles.formSection}>
      <div className={styles.formRow}>
        <label className={styles.fieldLabel}>{t('chatsocket.social_media_auth.facebook_page')}</label>
        <SelectSquare
          backgroundColor="#F3F3F3"
          border
          borderColor="#E3E3E3"
          placeholder={t('chatsocket.social_media_auth.facebook_page_placeholder')}
          arrowDirection="down"
          options={facebookPages || []}
          defaultSelected={
            facebookSettings?.facebookPageToken || { label: '', value: '' }
          }
          onClickItem={(value) => handleUpdate({ facebookPageToken: value })}
          disabled={isReadOnly}
        />
      </div>

      <div className={styles.formRow}>
        <label className={styles.fieldLabel}>{t('chatsocket.social_media_auth.publication_type')}</label>
        <MultiplySelectSquare
          backgroundColor="#F3F3F3"
          border
          borderColor="#E3E3E3"
          placeholder={t('chatsocket.social_media_auth.publication_type_placeholder')}
          arrowDirection="down"
          variante="gray"
          options={[
            { id: 2, label: t('chatsocket.social_media_auth.feed'), value: 'FEED' },
            { id: 3, label: t('chatsocket.social_media_auth.stories'), value: 'STORIES' },
          ]}
          fontSize={12}
          defaultSelected={facebookSettings?.facebookItems || []}
          onClickItem={(value) => handleUpdate({ facebookItems: value })}
          disabled={isReadOnly}
        />
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className={styles.formSection}>
      <p className={styles.helperLine}>
        {t('chatsocket.social_media_auth.no_config')}
      </p>
    </div>
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.popup_content}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.popup_header}>
          <div className={styles.headerLeft}>
            {network.icon && (
              <div className={styles.headerIcon}>{network.icon}</div>
            )}
            <h2 className={styles.title}>{t('chatsocket.social_media_auth.configure_title', { name: network.name })}</h2>
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('chatsocket.social_media_auth.close')}
          >
            <Cross />
          </button>
        </div>

        <div className={styles.popup_content_data}>
          <p className={styles.description}>
            {t('chatsocket.social_media_auth.configure_description', { name: network.name })}
          </p>

          {networkKey === 'tiktok' && renderTiktokFields()}
          {networkKey === 'instagram' && renderInstagramFields()}
          {networkKey === 'facebook' && renderFacebookFields()}
          {networkKey !== 'tiktok' &&
            networkKey !== 'instagram' &&
            networkKey !== 'facebook' &&
            renderDefault()}

          <div className={styles.actionsRow}>
            <button className={styles.secondaryButton} onClick={onClose}>
              {t('chatsocket.social_media_auth.cancel')}
            </button>
            <button
              className={styles.connectButton}
              onClick={handleSave}
              disabled={isReadOnly}
            >
              {t('chatsocket.social_media_auth.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
